import { useEffect, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_BASE ||
  "https://express-railway-api-production.up.railway.app";
const CONVERSIONS_URL = process.env.REACT_APP_CONVERSIONS_URL ||
  `${API_BASE.replace(/\/$/, "")}/events`;
const CUSTOM_FIELDS = [
  "content_name", "content_category", "content_ids", "content_type",
  "value", "currency", "destination",
];
const HASH_FIELDS = ["em", "ph", "fn", "ln"];

function getCookieValue(name) {
  const cookie = document.cookie.split("; ").find((item) => item.startsWith(`${name}=`));
  return cookie ? cookie.slice(name.length + 1) : "";
}

function setTrackingCookie(name, value) {
  document.cookie = `${name}=${value}; Max-Age=7776000; Path=/; SameSite=Lax; Secure`;
}

/** SHA-256 hex digest, as required by Meta for hashed user data (em/ph) */
async function sha256Hex(value) {
  if (typeof window === "undefined" || !window.crypto?.subtle) return "";
  const bytes = new TextEncoder().encode(value);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Normalize + hash an email per Meta's matching spec (lowercase, trimmed) */
async function hashEmail(email) {
  if (!email) return "";
  return sha256Hex(email.trim().toLowerCase());
}

/** Normalize + hash a phone number per Meta's matching spec (digits only, no leading zeros) */
async function hashPhone(phone) {
  if (!phone) return "";
  const digits = phone.replace(/[^0-9]/g, "").replace(/^0+/, "");
  if (!digits) return "";
  return sha256Hex(digits);
}

/** Normalize + hash a name (first or last) per Meta's matching spec (lowercase, trimmed) */
async function hashName(name) {
  if (!name) return "";
  return sha256Hex(name.trim().toLowerCase());
}

function sendMetaEvent(eventName, eventData = {}, options = {}) {
  if (typeof window === "undefined") return;

  const host = window.location.hostname.replace(/^www\./, "");
  if (host !== "donghungvienbakery.com") return;

  const eventID = window.crypto?.randomUUID?.() ||
    `event_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  const customData = Object.fromEntries(
    CUSTOM_FIELDS.filter((field) => eventData[field] !== undefined)
      .map((field) => [field, eventData[field]])
  );
  if (Array.isArray(customData.content_ids)) {
    customData.content_ids = customData.content_ids.map((id) => String(id));
  }
  if (typeof customData.value === "string" && /^\$?\d+(?:\.\d+)?$/.test(customData.value)) {
    customData.value = Number(customData.value.replace(/^\$/, ""));
  }
  // Hashes belong in CAPI user_data, never browser custom event parameters.
  const matchingData = Object.fromEntries(
    HASH_FIELDS.filter((field) => eventData[field]).map((field) => [field, eventData[field]])
  );

  if (typeof window.fbq === "function") {
    try {
      window.fbq("track", eventName, customData, { eventID });
    } catch (error) {
      console.error("Meta browser event delivery failed:", error.name);
    }
  }

  let fbp = getCookieValue("_fbp");
  let fbc = getCookieValue("_fbc");
  const fbclid = new URLSearchParams(window.location.search).get("fbclid");
  if (!fbp) {
    fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 10_000_000_000)}`;
    setTrackingCookie("_fbp", fbp);
  }
  if (fbclid && /^[A-Za-z0-9._-]{1,512}$/.test(fbclid)) {
    if (!fbc.endsWith(`.${fbclid}`)) {
      fbc = `fb.1.${Date.now()}.${fbclid}`;
      setTrackingCookie("_fbc", fbc);
    }
  }
  const body = {
    ...customData,
    ...matchingData,
    event_name: eventName,
    event_id: eventID,
    event_source_url: `${window.location.origin}${window.location.pathname}`,
    fbp,
    ...(fbc && { fbc }),
  };

  return fetch(CONVERSIONS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: options.keepalive ?? false,
    credentials: "omit",
  }).then(async (response) => {
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const result = await response.json();
    if (result.events_received !== 1) throw new Error("Meta did not acknowledge the event.");
    return result;
  }).catch((error) => {
    console.error("Meta server event delivery failed:", error.message);
  });
}

/**
 * useMetaTracking("PageView", {...})
 * - Sends browser and server events with the same ID for Meta deduplication
 */
const useMetaTracking = (eventName, eventData = {}) => {
  // eventData is often passed as a new inline object each render;
  // track latest value via ref so the effect below can fire once per eventName
  const eventDataRef = useRef(eventData);
  eventDataRef.current = eventData;

  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    sendMetaEvent(eventName, eventDataRef.current);
  }, [eventName]);
};

export default useMetaTracking;
export { sendMetaEvent, hashEmail, hashPhone, hashName };